import { Check, MapPin } from "lucide-react";
import { useState } from "react";
import MapModal from "../../../modals/MapModal";
import { GoogleMap, Marker, useLoadScript } from "@react-google-maps/api";
import SmallInputField from "./SmallInputField";

const TinyMapPreview = ({ coordinates, onEdit }) => {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_KEY || "YOUR_API_KEY",
  });

  if (!isLoaded) {
    return (
      <div className="w-full h-48 bg-gray-100 rounded-lg flex items-center justify-center">
        <p className="text-sm text-text-secondary">Loading map preview...</p>
      </div>
    );
  }

  return (
    <div className="relative w-full h-48 rounded-lg overflow-hidden border-2 border-status-success/30 shadow-sm">
      <GoogleMap
        zoom={14}
        center={{ lat: coordinates[1], lng: coordinates[0] }}
        mapContainerClassName="w-full h-full"
        options={{
          disableDefaultUI: true,
          gestureHandling: "none",
          zoomControl: false,
        }}
      >
        <Marker position={{ lat: coordinates[1], lng: coordinates[0] }} />
      </GoogleMap>

      {/* Edit Overlay */}
      <div className="absolute top-2 right-2">
        <button
          onClick={onEdit}
          className="bg-white px-3 py-1.5 rounded-lg shadow-md border border-border hover:bg-gray-50 transition-colors text-sm font-medium text-btn-primary flex items-center gap-1"
        >
          <MapPin className="w-3.5 h-3.5" />
          Edit
        </button>
      </div>
    </div>
  );
};

// Main Location Selector Component
const LocationSelector = ({ value, onChange }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openMap = () => setIsModalOpen(true);
  const closeMap = () => setIsModalOpen(false);

  const handleManualChange = (field, fieldValue) => {
    onChange({
      ...value,
      address: {
        ...value.address,
        [field]: fieldValue,
      },
    });
  };

  const initialLocation =
    value?.coordinates?.length > 0
      ? { lat: value.coordinates[1], lng: value.coordinates[0] }
      : null;

  const hasMapSelection = value?.coordinates?.length > 0;

  return (
    <div className="space-y-6">
      {/* Select on Map Button */}
      <div>
        {!hasMapSelection && (
          <button
            onClick={openMap}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-primary text-primary font-medium hover:bg-primary/20 active:scale-[.98] transition-all"
          >
            <MapPin className="w-4 h-4" />
            Select on Map{" "}
          </button>
        )}
      </div>

      {/* Map Preview - Show after selection */}
      {hasMapSelection && (
        <>
          <TinyMapPreview coordinates={value.coordinates} onEdit={openMap} />

          {/* Divider */}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-border"></div>
            <span className="text-sm font-medium text-text-secondary">
              Confirm Address
            </span>
            <div className="flex-1 h-px bg-border"></div>
          </div>

          {/* Manual Address Input */}
          <div className="space-y-4">
            <SmallInputField
              label="Street Address"
              name="street"
              value={value?.address?.street || ""}
              onChange={(e) => handleManualChange("street", e.target.value)}
              placeholder="Enter street address"
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <SmallInputField
                label="Locality"
                name="locality"
                value={value?.address?.locality || ""}
                onChange={(e) => handleManualChange("locality", e.target.value)}
                placeholder="Area/Locality"
              />

              <SmallInputField
                label="City"
                name="city"
                value={value?.address?.city || ""}
                onChange={(e) => handleManualChange("city", e.target.value)}
                placeholder="Enter city"
                required
              />

              <SmallInputField
                label="State"
                name="state"
                value={value?.address?.state || ""}
                onChange={(e) => handleManualChange("state", e.target.value)}
                placeholder="Enter state"
                required
              />

              <SmallInputField
                label="PIN Code"
                name="pincode"
                type="text"
                value={value?.address?.pincode || ""}
                onChange={(e) => handleManualChange("pincode", e.target.value)}
                placeholder="Enter PIN code"
              />
            </div>
          </div>
        </>
      )}

      {/* Map Modal */}
      <MapModal
        isOpen={isModalOpen}
        onClose={closeMap}
        initialLocation={initialLocation}
        onSelect={(coords, addressData) => {
          // console.log("coords:", coords);
          // console.log("addressData:", addressData);
          onChange({
            type: "Point",
            coordinates: [coords.lng, coords.lat],
            address: addressData,
          });
          closeMap();
        }}
      />
    </div>
  );
};
export default LocationSelector;
